import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Input, Label } from "reactstrap";
import { canUseGiRegistry } from "../../helpers/geoEnabled";
import { listFrozenGis } from "../../services/geoGiApi";

type Props = {
  value?: string;
  onChange: (giId: string) => void;
  required?: boolean;
  label?: string;
  disabled?: boolean;
  fallbackLabel?: string;
};

const readRole = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const GiFrozenSelect = ({
  value = "",
  onChange,
  required = false,
  label = "GI",
  disabled = false,
  fallbackLabel,
}: Props) => {
  const role = readRole();
  const [options, setOptions] = useState<{ id: string; name: string; applicationNumber?: string; state?: string }[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      return;
    }
    listFrozenGis().then((res: any) => {
      if (!res?.success) {
        setError(res?.message || "Could not load GIs");
        setOptions([]);
        return;
      }
      const rows = Array.isArray(res.data) ? res.data : [];
      setOptions(rows.map((row: any) => ({
        id: row.id || row._id,
        name: row.name,
        applicationNumber: row.applicationNumber,
        state: row.state,
      })));
      setError("");
    });
  }, [role]);

  if (!canUseGiRegistry(role)) {
    return null;
  }

  return (
    <>
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      <Input type="select" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{required ? "Select GI" : "Select GI (optional)"}</option>
        {value && !options.some((opt) => opt.id === value) ? (
          <option value={value}>{fallbackLabel || value}</option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.name}
            {opt.applicationNumber ? ` (${opt.applicationNumber})` : ""}
            {opt.state ? ` · ${opt.state}` : ""}
          </option>
        ))}
      </Input>
      {error ? <p className="text-danger" style={{ fontSize: 13 }}>{error}</p> : null}
      {options.length === 0 && !error ? (
        <p className="text-muted" style={{ fontSize: 13 }}>
          No GIs in your studio yet. Add one from the{" "}
          <Link to="/gi-registry">GI Registry</Link>.
        </p>
      ) : null}
    </>
  );
};

export default GiFrozenSelect;
